import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Layers, Play, Eye } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { useActiveTenant } from '../contexts/tenant'

type Wave = {
  id: string; number: string; waveDate: string; status: string
  separatorId: string | null; createdAt: string; orderCount: number
}
type EligibleOrder = {
  id: string; orderNumber: string; carrier: string | null
  customerName: string | null; originChannel: string | null; emissionDate: string | null
  hasDanfe: boolean; hasLabel: boolean; complete: boolean; marketplace: boolean
  itens: number; semItens: boolean
}

const statusLabel: Record<string, string> = {
  open: 'Aberta', picking: 'Em separação', checked: 'Conferida', closed: 'Fechada', cancelled: 'Cancelada',
}
const statusVariant: Record<string, 'default' | 'info' | 'warning' | 'success' | 'danger'> = {
  open: 'default', picking: 'info', checked: 'warning', closed: 'success', cancelled: 'danger',
}

export function PickingPage() {
  const tenantId = useActiveTenant()
  const qc = useQueryClient()
  const navigate = useNavigate()

  const { data: eligible = [] } = useQuery<EligibleOrder[]>({
    queryKey: ['picking-eligible', tenantId],
    queryFn: async () => (await api.get('/picking/eligible', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  const { data: waves = [], isLoading } = useQuery<Wave[]>({
    queryKey: ['picking-waves', tenantId],
    queryFn: async () => (await api.get('/picking/waves', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  const [channel, setChannel] = useState('') // '' = todos os canais

  const generate = useMutation({
    mutationFn: async (ch: string) => (await api.post('/picking/waves', { tenantId, channel: ch || undefined })).data,
    onSuccess: (w: { id: string }) => {
      qc.invalidateQueries({ queryKey: ['picking-waves', tenantId] })
      qc.invalidateQueries({ queryKey: ['picking-eligible', tenantId] })
      if (w?.id) navigate(`/picking/${w.id}`)
    },
  })

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  // Pendentes agrupados por canal de venda (ML tem horário de coleta diferente)
  const byChannel = eligible.reduce<Record<string, number>>((acc, o) => {
    const c = o.originChannel ?? 'Sem canal'
    acc[c] = (acc[c] ?? 0) + 1
    return acc
  }, {})
  const channels = Object.entries(byChannel).sort((a, b) => b[1] - a[1])
  const countSelected = channel ? (byChannel[channel] ?? 0) : eligible.length
  const shown = channel ? eligible.filter(o => (o.originChannel ?? 'Sem canal') === channel) : eligible
  const chip = (active: boolean) =>
    `rounded-full px-3 py-1 text-xs font-medium ${active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Separação (Picking)</h2>
          <p className="text-sm text-gray-500">Ondas de separação · pedidos idênticos agrupados</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
            value={channel}
            onChange={e => setChannel(e.target.value)}
          >
            <option value="">Todos os canais ({eligible.length})</option>
            {channels.filter(([c]) => c !== 'Sem canal').map(([c, n]) => (
              <option key={c} value={c}>{c} ({n})</option>
            ))}
          </select>
          <Button size="sm" disabled={countSelected === 0} loading={generate.isPending} onClick={() => generate.mutate(channel)}>
            <Play className="h-4 w-4" /> Gerar onda ({countSelected})
          </Button>
        </div>
      </div>

      {generate.error && (
        <p className="text-sm text-red-600">{(generate.error as any)?.response?.data?.detail ?? (generate.error as any)?.response?.data?.title ?? 'Erro ao gerar onda'}</p>
      )}

      {/* Pedidos elegíveis */}
      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">Pedidos liberados</span>
          <span className="text-xs text-gray-400">
            {eligible.length} aguardando separação · <span className="text-green-600 font-medium">{eligible.filter(o => o.complete).length} completos</span>
            {eligible.some(o => !o.complete) && <> · <span className="text-amber-600 font-medium">{eligible.filter(o => !o.complete).length} sem etiqueta/NF</span></>}
            {eligible.some(o => o.semItens) && <> · <span className="text-red-600 font-medium">{eligible.filter(o => o.semItens).length} sem produto</span></>}
          </span>
        </CardHeader>
        <CardContent className="p-0">
          {channels.length > 0 && (
            <div className="flex flex-wrap gap-2 border-b border-gray-100 px-5 py-3">
              <button onClick={() => setChannel('')} className={chip(channel === '')}>Todos ({eligible.length})</button>
              {channels.map(([c, n]) => (
                <button key={c} onClick={() => setChannel(c === channel ? '' : c)} className={chip(channel === c)}>{c} ({n})</button>
              ))}
            </div>
          )}
          {shown.length === 0 ? (
            <p className="px-5 py-6 text-center text-sm text-gray-400">
              Nenhum pedido liberado. Pedidos entram aqui quando a etiqueta é gerada (status reserved).
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Pedido</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Cliente</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Canal</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Transportadora</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Documentos</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Emissão</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {shown.map(o => (
                    <tr key={o.id} className="hover:bg-gray-50">
                      <td className="px-5 py-3 font-medium text-gray-900">{o.orderNumber}</td>
                      <td className="px-5 py-3 text-gray-600">{o.customerName ?? '—'}</td>
                      <td className="px-5 py-3 text-gray-600">{o.originChannel ?? '—'}</td>
                      <td className="px-5 py-3 text-gray-600">{o.carrier ?? '—'}</td>
                      <td className="px-5 py-3">
                        <div className="flex gap-1.5">
                          {o.semItens && <Badge variant="danger">Sem produto</Badge>}
                          <Badge variant={o.hasDanfe ? 'success' : 'warning'}>NF{o.hasDanfe ? ' ✓' : ' ✗'}</Badge>
                          {o.marketplace && !o.hasLabel
                            ? <Badge variant="info">Etiq externa</Badge>
                            : <Badge variant={o.hasLabel ? 'success' : 'warning'}>Etiq{o.hasLabel ? ' ✓' : ' ✗'}</Badge>}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-gray-500">{o.emissionDate ? new Date(o.emissionDate).toLocaleDateString('pt-BR') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ondas */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-900">Ondas</span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-8 text-center text-sm text-gray-400">Carregando...</p>
          ) : waves.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-gray-400">Nenhuma onda gerada</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Onda</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Data</th>
                    <th className="px-5 py-3 text-right font-medium text-gray-600">Pedidos</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {waves.map(w => (
                    <tr key={w.id} className="hover:bg-gray-50">
                      <td className="px-5 py-3 font-mono font-bold text-gray-900">{w.number}</td>
                      <td className="px-5 py-3 text-gray-600">{new Date(w.waveDate).toLocaleDateString('pt-BR')}</td>
                      <td className="px-5 py-3 text-right font-semibold">{w.orderCount}</td>
                      <td className="px-5 py-3"><Badge variant={statusVariant[w.status] ?? 'default'}>{statusLabel[w.status] ?? w.status}</Badge></td>
                      <td className="px-5 py-3">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/picking/${w.id}`)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
